class HeroCarousel extends HTMLElement {
  constructor() {
    super();
    this.currentIndex = 0;
    this.isAnimating = false;
    this.autoplayPaused = false;
    this.userPaused = false;
    this.autoplayTimer = null;
    this.progressStart = 0;
    this.touchStartX = 0;
    this.touchStartY = 0;
    this.touchMoved = false;
    this.blockSelectHandler = null;
    this.blockDeselectHandler = null;
  }

  connectedCallback() {
    this.slidesContainer = this.querySelector('.hero-carousel__slides');
    this.slides = this.querySelectorAll('.hero-carousel__slide');

    if (!this.slides.length) return;

    this.duration = parseInt(this.dataset.duration) || 800;
    this.speed = parseInt(this.dataset.speed) || 5;
    this.autoplayEnabled = this.dataset.autoplay === 'true';
    this.pauseOnHover = this.dataset.pauseHover !== 'false';
    this.swipeEnabled = this.dataset.enableSwipe !== 'false';

    this.style.setProperty('--hero-carousel-duration', this.duration + 'ms');
    this.style.setProperty('--hero-carousel-speed', this.speed + 's');

    this.setupAria();
    this.setupNavigation();
    this.setupInteractionEvents();

    if (this.autoplayEnabled && this.slides.length > 1) {
      this.setupAutoplayEvents();
      this.startAutoplay();
    }

    this.updateNav();

    this.blockSelectHandler = this.onBlockSelect.bind(this);
    this.blockDeselectHandler = this.onBlockDeselect.bind(this);
    document.addEventListener('shopify:block:select', this.blockSelectHandler);
    document.addEventListener('shopify:block:deselect', this.blockDeselectHandler);
  }

  disconnectedCallback() {
    this.stopAutoplay();
    this.stopProgressAnimation();
    if (this.blockSelectHandler) {
      document.removeEventListener('shopify:block:select', this.blockSelectHandler);
    }
    if (this.blockDeselectHandler) {
      document.removeEventListener('shopify:block:deselect', this.blockDeselectHandler);
    }
  }

  setupAria() {
    this.slides.forEach((slide, index) => {
      slide.setAttribute('role', 'group');
      slide.setAttribute('aria-roledescription', 'slide');
      slide.setAttribute('aria-label', `Slide ${index + 1} of ${this.slides.length}`);
    });
    this.setSlideVisibility();
  }

  setSlideVisibility() {
    this.slides.forEach((slide, index) => {
      const isActive = index === this.currentIndex;
      slide.setAttribute('aria-hidden', !isActive);
      const links = slide.querySelectorAll('a');
      links.forEach((link) => {
        if (isActive) {
          link.removeAttribute('tabindex');
        } else {
          link.setAttribute('tabindex', '-1');
        }
      });
    });
  }

  setupNavigation() {
    this.prevBtn = this.querySelector('.hero-carousel__btn--prev');
    this.nextBtn = this.querySelector('.hero-carousel__btn--next');

    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.goToPrev();
      });
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.goToNext();
      });
    }

    const controlLinks = this.querySelectorAll('.hero-carousel__dot, .hero-carousel__number, .hero-carousel__bar');
    controlLinks.forEach((link, index) => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        this.goTo(index);
      });
    });

    this.autoplayBtn = this.querySelector('.hero-carousel__autoplay');
    if (this.autoplayBtn) {
      this.autoplayBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggleAutoplay();
      });
    }
  }

  setupInteractionEvents() {
    if (this.swipeEnabled) {
      this.addEventListener('touchstart', (e) => {
        this.touchStartX = e.changedTouches[0].screenX;
        this.touchStartY = e.changedTouches[0].screenY;
        this.touchMoved = false;
      }, { passive: true });

      this.addEventListener('touchmove', (e) => {
        if (!this.touchStartX) return;
        const deltaX = Math.abs(e.changedTouches[0].screenX - this.touchStartX);
        const deltaY = Math.abs(e.changedTouches[0].screenY - this.touchStartY);
        if (deltaX > deltaY && deltaX > 10) {
          this.touchMoved = true;
        }
      }, { passive: true });

      this.addEventListener('touchend', (e) => {
        if (!this.touchStartX || !this.touchMoved) return;
        const deltaX = e.changedTouches[0].screenX - this.touchStartX;
        if (Math.abs(deltaX) > 50) {
          if (deltaX > 0) {
            this.goToPrev();
          } else {
            this.goToNext();
          }
        }
        this.touchStartX = 0;
        this.touchStartY = 0;
        this.touchMoved = false;
      }, { passive: true });
    }

    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.goToPrev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.goToNext();
      }
    });

    this.slides.forEach((slide) => {
      const link = slide.dataset.link;
      if (link) {
        slide.addEventListener('click', (e) => {
          if (e.target.closest('a, button')) return;
          window.location.href = link;
        });
      }
    });
  }

  setupAutoplayEvents() {
    if (this.pauseOnHover) {
      this.addEventListener('mouseenter', () => this.pauseAutoplay());
      this.addEventListener('mouseleave', () => this.resumeAutoplay());
    }

    this.addEventListener('focusin', () => this.pauseAutoplay());
    this.addEventListener('focusout', (e) => {
      if (!this.contains(e.relatedTarget)) this.resumeAutoplay();
    });

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.pauseAutoplay();
      } else if (!this.userPaused) {
        this.resumeAutoplay();
      }
    });
  }

  goTo(index) {
    if (this.isAnimating || index < 0 || index >= this.slides.length) return;
    if (index === this.currentIndex) return;

    this.isAnimating = true;
    const currentSlide = this.slides[this.currentIndex];
    const nextSlide = this.slides[index];

    this.slides.forEach((s) => s.classList.remove('is-animating-content'));
    currentSlide.classList.add('is-leaving');
    currentSlide.classList.remove('is-active');
    nextSlide.classList.add('is-active');
    nextSlide.classList.add('is-animating-content');

    this.currentIndex = index;
    this.updateNav();
    this.setSlideVisibility();
    this.resetAutoplay();

    this.dispatchEvent(new CustomEvent('slideChanged', { detail: { currentIndex: index } }));

    setTimeout(() => {
      currentSlide.classList.remove('is-leaving');
      this.isAnimating = false;
    }, this.duration);
  }

  goToNext() {
    this.goTo((this.currentIndex + 1) % this.slides.length);
  }

  goToPrev() {
    this.goTo((this.currentIndex - 1 + this.slides.length) % this.slides.length);
  }

  updateNav() {
    const currentEl = this.querySelector('.hero-carousel__counter-current');
    const totalEl = this.querySelector('.hero-carousel__counter-total');
    if (currentEl) {
      currentEl.textContent = String(this.currentIndex + 1).padStart(2, '0');
    }
    if (totalEl) {
      totalEl.textContent = String(this.slides.length).padStart(2, '0');
    }

    const dots = this.querySelectorAll('.hero-carousel__dot');
    dots.forEach((dot, i) => {
      const isActive = i === this.currentIndex;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');
      const fill = dot.querySelector('.hero-carousel__dot-fill');
      if (fill && !isActive) fill.style.width = '0%';
    });

    const numbers = this.querySelectorAll('.hero-carousel__number');
    numbers.forEach((num, i) => {
      num.classList.toggle('is-active', i === this.currentIndex);
      num.setAttribute('aria-current', i === this.currentIndex ? 'true' : 'false');
    });

    const bars = this.querySelectorAll('.hero-carousel__bar');
    bars.forEach((bar, i) => {
      const isActive = i === this.currentIndex;
      bar.classList.toggle('is-active', isActive);
      bar.setAttribute('aria-current', isActive ? 'true' : 'false');
      const fill = bar.querySelector('.hero-carousel__bar-fill');
      if (fill && !isActive) fill.style.width = '0%';
    });

    if (this.prevBtn) this.prevBtn.removeAttribute('disabled');
    if (this.nextBtn) this.nextBtn.removeAttribute('disabled');
  }

  startAutoplay() {
    if (this.slides.length < 2) return;
    this.autoplayPaused = false;
    this.userPaused = false;
    this.slidesContainer.setAttribute('aria-live', 'off');
    this.startProgressAnimation();
    this.autoplayTimer = setTimeout(() => {
      if (!this.autoplayPaused) this.goToNext();
    }, this.speed * 1000);
  }

  pauseAutoplay() {
    if (this.autoplayPaused) return;
    this.autoplayPaused = true;
    this.pauseProgressAnimation();
    if (this.autoplayTimer) {
      clearTimeout(this.autoplayTimer);
      this.autoplayTimer = null;
    }
    this.slidesContainer.setAttribute('aria-live', 'polite');
  }

  resumeAutoplay() {
    if (!this.autoplayPaused) return;
    if (this.userPaused) return;
    this.autoplayPaused = false;
    this.slidesContainer.setAttribute('aria-live', 'off');
    this.resumeProgressAnimation();
    const currentScale = Math.min(parseFloat(this.progressFill && this.progressFill.style.width) || 0, 100) / 100;
    const remaining = Math.max(0, (this.speed * 1000) - currentScale * (this.speed * 1000));
    this.autoplayTimer = setTimeout(() => {
      if (!this.autoplayPaused) this.goToNext();
    }, remaining);
  }

  stopAutoplay() {
    if (this.autoplayTimer) {
      clearTimeout(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  resetAutoplay() {
    if (!this.autoplayEnabled) return;
    this.stopAutoplay();
    if (!this.autoplayPaused) {
      this.startProgressAnimation();
      this.autoplayTimer = setTimeout(() => {
        if (!this.autoplayPaused) this.goToNext();
      }, this.speed * 1000);
    }
  }

  toggleAutoplay() {
    if (!this.autoplayBtn) return;
    const isPlaying = !this.autoplayBtn.classList.contains('is-paused');
    this.autoplayBtn.classList.toggle('is-paused');
    if (isPlaying) {
      this.userPaused = true;
      this.pauseAutoplay();
      this.autoplayBtn.setAttribute('aria-label', 'Play slideshow');
    } else {
      this.userPaused = false;
      this.autoplayBtn.setAttribute('aria-label', 'Pause slideshow');
      this.resumeAutoplay();
    }
  }

  startProgressAnimation() {
    const activeBar = this.querySelector('.hero-carousel__bar.is-active .hero-carousel__bar-fill');
    const activeDot = this.querySelector('.hero-carousel__dot.is-active .hero-carousel__dot-fill');
    const fill = activeBar || activeDot || this.querySelector('.hero-carousel__progress-fill');
    if (!fill) return;
    this.progressFill = fill;
    this.stopProgressAnimation();
    this.progressStart = performance.now();
    this._runProgress();
  }

  _runProgress() {
    const fill = this.progressFill;
    if (!fill) return;
    const tick = () => {
      if (this.autoplayPaused) return;
      const elapsed = performance.now() - this.progressStart;
      const scale = Math.min(elapsed / (this.speed * 1000), 1);
      fill.style.width = (scale * 100) + '%';
      if (scale < 1) {
        this._progressRAF = requestAnimationFrame(tick);
      }
    };
    this._progressRAF = requestAnimationFrame(tick);
  }

  pauseProgressAnimation() {
    if (this._progressRAF) {
      cancelAnimationFrame(this._progressRAF);
      this._progressRAF = null;
    }
    if (this.progressFill) {
      const elapsed = performance.now() - this.progressStart;
      this.progressFill.style.width = Math.min(elapsed / (this.speed * 1000), 1) * 100 + '%';
    }
  }

  resumeProgressAnimation() {
    this.progressStart = performance.now() - (parseFloat(this.progressFill && this.progressFill.style.width) || 0) / 100 * (this.speed * 1000);
    this._runProgress();
  }

  stopProgressAnimation() {
    if (this._progressRAF) {
      cancelAnimationFrame(this._progressRAF);
      this._progressRAF = null;
    }
  }

  onBlockSelect(event) {
    const slide = event.target.closest('.hero-carousel__slide');
    if (!slide || !this.contains(slide)) return;
    this.pauseAutoplay();
    const index = Array.from(this.slides).indexOf(slide);
    if (index >= 0) {
      setTimeout(() => this.goTo(index), 200);
    }
  }

  onBlockDeselect(event) {
    const slide = event.target.closest('.hero-carousel__slide');
    if (!slide || !this.contains(slide)) return;
    if (this.autoplayEnabled && !this.userPaused && this.autoplayPaused) {
      this.resumeAutoplay();
    }
  }
}

customElements.define('hero-carousel', HeroCarousel);
